/**
 * Type-safe i18n message keys for the Ease Experience platform.
 *
 * All NEW UI text must use these keys via useTranslation().
 * Keys are organized by module to match the translation file structure.
 *
 * Usage:
 *   const { t } = useTranslation();
 *   t(MSG.nav.dashboard)  // type-checked at compile time
 *   t(MSG.persona.prompt.title)
 */

// ---------------------------------------------------------------------------
// Message key constants — type-safe, autocomplete-friendly
// ---------------------------------------------------------------------------

export const MSG = {
  // Navigation
  nav: {
    dashboard: "nav.dashboard",
    crm: "nav.crm",
    email: "nav.email",
    pages: "nav.pages",
    workflows: "nav.workflows",
    analytics: "nav.analytics",
    settings: "nav.settings",
    admin: "nav.admin",
    inbox: "nav.inbox",
    bookings: "nav.bookings",
    invoices: "nav.invoices",
    messages: "nav.messages",
  },

  // Common actions
  common: {
    save: "common.save",
    cancel: "common.cancel",
    delete: "common.delete",
    edit: "common.edit",
    create: "common.create",
    search: "common.search",
    loading: "common.loading",
    noResults: "common.noResults",
    confirm: "common.confirm",
    back: "common.back",
    next: "common.next",
    close: "common.close",
    actions: "common.actions",
    status: "common.status",
    name: "common.name",
    email: "common.email",
    phone: "common.phone",
    enabled: "common.enabled",
    disabled: "common.disabled",
    active: "common.active",
    inactive: "common.inactive",
  },

  // Persona system (B2-B5)
  persona: {
    prompt: {
      title: "persona.prompt.title",
      subtitle: "persona.prompt.subtitle",
      operator: "persona.prompt.operator",
      operatorDesc: "persona.prompt.operatorDesc",
      buyer: "persona.prompt.buyer",
      buyerDesc: "persona.prompt.buyerDesc",
      cta: "persona.prompt.cta",
    },
    labels: {
      operator: "persona.labels.operator",
      buyer: "persona.labels.buyer",
      agency: "persona.labels.agency",
      affiliate: "persona.labels.affiliate",
    },
    settings: {
      title: "persona.settings.title",
      description: "persona.settings.description",
      currentPersona: "persona.settings.currentPersona",
      switchLabel: "persona.settings.switchLabel",
    },
  },

  // Empty states per persona (B4)
  empty: {
    operator: {
      contacts: "empty.operator.contacts",
      deals: "empty.operator.deals",
      campaigns: "empty.operator.campaigns",
      workflows: "empty.operator.workflows",
      pages: "empty.operator.pages",
    },
    buyer: {
      bookings: "empty.buyer.bookings",
      messages: "empty.buyer.messages",
      invoices: "empty.buyer.invoices",
    },
  },

  // Pricing page (B6)
  pricing: {
    title: "pricing.title",
    subtitle: "pricing.subtitle",
    monthly: "pricing.monthly",
    annual: "pricing.annual",
    annualSave: "pricing.annualSave",
    getStarted: "pricing.getStarted",
    contactSales: "pricing.contactSales",
    currentPlan: "pricing.currentPlan",
    popular: "pricing.popular",
    perMonth: "pricing.perMonth",
    features: "pricing.features",
    allFeatures: "pricing.allFeatures",
    free: {
      name: "pricing.free.name",
      desc: "pricing.free.desc",
    },
    starter: {
      name: "pricing.starter.name",
      desc: "pricing.starter.desc",
    },
    growth: {
      name: "pricing.growth.name",
      desc: "pricing.growth.desc",
    },
    scale: {
      name: "pricing.scale.name",
      desc: "pricing.scale.desc",
    },
    enterprise: {
      name: "pricing.enterprise.name",
      desc: "pricing.enterprise.desc",
    },
  },

  // Auth
  auth: {
    login: "auth.login",
    signup: "auth.signup",
    logout: "auth.logout",
    forgotPassword: "auth.forgotPassword",
  },

  // Errors
  errors: {
    generic: "errors.generic",
    notFound: "errors.notFound",
    unauthorized: "errors.unauthorized",
    networkError: "errors.networkError",
    validationError: "errors.validationError",
    serverError: "errors.serverError",
  },

  admin: {
    analytics: {
      title: "admin.analytics.title",
      description: "admin.analytics.description",
      changeVsPrevMonth: "admin.analytics.changeVsPrevMonth",
      kpis: {
        totalMrr: "admin.analytics.kpis.totalMrr",
        totalContacts: "admin.analytics.kpis.totalContacts",
        totalEvents: "admin.analytics.kpis.totalEvents",
        activeTenants: "admin.analytics.kpis.activeTenants",
        churnRate: "admin.analytics.kpis.churnRate",
        avgMrrPerTenant: "admin.analytics.kpis.avgMrrPerTenant",
        payingTenants: "admin.analytics.kpis.payingTenants",
        acrossAllTenants: "admin.analytics.kpis.acrossAllTenants",
        last30Days: "admin.analytics.kpis.last30Days",
        activeLast7Days: "admin.analytics.kpis.activeLast7Days",
        monthly: "admin.analytics.kpis.monthly",
        payingTenantsOnly: "admin.analytics.kpis.payingTenantsOnly",
      },
      growth: {
        title: "admin.analytics.growth.title",
        newTenantsPerMonth: "admin.analytics.growth.newTenantsPerMonth",
        chartRendersProvisioned: "admin.analytics.growth.chartRendersProvisioned",
        mrrGrowth: "admin.analytics.growth.mrrGrowth",
        chartRendersSubscriptions: "admin.analytics.growth.chartRendersSubscriptions",
      },
      topTenants: {
        title: "admin.analytics.topTenants.title",
        byRevenue: "admin.analytics.topTenants.byRevenue",
        byContacts: "admin.analytics.topTenants.byContacts",
        byActivity: "admin.analytics.topTenants.byActivity",
        noDataYet: "admin.analytics.topTenants.noDataYet",
      },
      platformHealth: {
        title: "admin.analytics.platformHealth.title",
        workerErrorRate: "admin.analytics.platformHealth.workerErrorRate",
        queueDepth: "admin.analytics.platformHealth.queueDepth",
        dbConnections: "admin.analytics.platformHealth.dbConnections",
        p95Latency: "admin.analytics.platformHealth.p95Latency",
      },
    },
    billing: {
      title: "admin.billing.title",
      searchPlaceholder: "admin.billing.searchPlaceholder",
      empty: "admin.billing.empty",
      stats: {
        totalMrr: "admin.billing.stats.totalMrr",
        activeSubscriptions: "admin.billing.stats.activeSubscriptions",
        pastDue: "admin.billing.stats.pastDue",
        trials: "admin.billing.stats.trials",
      },
      table: {
        tenant: "admin.billing.table.tenant",
        plan: "admin.billing.table.plan",
        status: "admin.billing.table.status",
        mrr: "admin.billing.table.mrr",
        nextBill: "admin.billing.table.nextBill",
      },
      actions: {
        overridePlan: "admin.billing.actions.overridePlan",
      },
      statuses: {
        active: "admin.billing.statuses.active",
        trialing: "admin.billing.statuses.trialing",
        pastDue: "admin.billing.statuses.pastDue",
        canceled: "admin.billing.statuses.canceled",
        suspended: "admin.billing.statuses.suspended",
      },
    },
    flags: {
      title: "admin.flags.title",
      createFlag: "admin.flags.createFlag",
      create: "admin.flags.create",
      cancel: "admin.flags.cancel",
      flagNamePlaceholder: "admin.flags.flagNamePlaceholder",
      descriptionPlaceholder: "admin.flags.descriptionPlaceholder",
      perTenantOverrides: "admin.flags.perTenantOverrides",
      enabled: "admin.flags.enabled",
      disabled: "admin.flags.disabled",
      addTenantOverride: "admin.flags.addTenantOverride",
      descriptions: {
        aiAssistant: "admin.flags.descriptions.aiAssistant",
        webinarSystem: "admin.flags.descriptions.webinarSystem",
        mcpServer: "admin.flags.descriptions.mcpServer",
        domainPurchasing: "admin.flags.descriptions.domainPurchasing",
        advancedAnalytics: "admin.flags.descriptions.advancedAnalytics",
      },
    },
    apiKeys: {
      title: "admin.apiKeys.title",
      configuredSummarySuffix: "admin.apiKeys.configuredSummarySuffix",
      encryptedAtRest: "admin.apiKeys.encryptedAtRest",
      securityNotice: "admin.apiKeys.securityNotice",
      lastUpdatedPrefix: "admin.apiKeys.lastUpdatedPrefix",
      inputs: {
        apiKey: "admin.apiKeys.inputs.apiKey",
        secretKey: "admin.apiKeys.inputs.secretKey",
      },
      actions: {
        saveAndEncrypt: "admin.apiKeys.actions.saveAndEncrypt",
        saving: "admin.apiKeys.actions.saving",
        cancel: "admin.apiKeys.actions.cancel",
        updateKey: "admin.apiKeys.actions.updateKey",
        configureKey: "admin.apiKeys.actions.configureKey",
      },
      messages: {
        saveSuccessSuffix: "admin.apiKeys.messages.saveSuccessSuffix",
        saveError: "admin.apiKeys.messages.saveError",
      },
      providers: {
        stripeDescription: "admin.apiKeys.providers.stripeDescription",
        stripeWebhookDescription: "admin.apiKeys.providers.stripeWebhookDescription",
        googleOauthDescription: "admin.apiKeys.providers.googleOauthDescription",
        metaAppDescription: "admin.apiKeys.providers.metaAppDescription",
        tiktokAppDescription: "admin.apiKeys.providers.tiktokAppDescription",
        openAiDescription: "admin.apiKeys.providers.openAiDescription",
        sesDescription: "admin.apiKeys.providers.sesDescription",
        telnyxDescription: "admin.apiKeys.providers.telnyxDescription",
      },
    },
    provision: {
      title: "admin.provision.title",
      createTenant: "admin.provision.createTenant",
      newTenant: "admin.provision.newTenant",
      businessName: "admin.provision.businessName",
      slug: "admin.provision.slug",
      plan: "admin.provision.plan",
      ownerEmail: "admin.provision.ownerEmail",
      ownerEmailPlaceholder: "admin.provision.ownerEmailPlaceholder",
      provisioning: "admin.provision.provisioning",
      tenantActions: "admin.provision.tenantActions",
      actionDescription: "admin.provision.actionDescription",
      actionWarning: "admin.provision.actionWarning",
      createFlowSummary: "admin.provision.createFlowSummary",
      actions: {
        createTenant: "admin.provision.actions.createTenant",
        cancel: "admin.provision.actions.cancel",
      },
      plans: {
        free: "admin.provision.plans.free",
        starter: "admin.provision.plans.starter",
        growth: "admin.provision.plans.growth",
        scale: "admin.provision.plans.scale",
      },
    },
    taxRates: {
      title: "admin.taxRates.title",
      description: "admin.taxRates.description",
      unavailableTitle: "admin.taxRates.unavailableTitle",
      unavailableDescription: "admin.taxRates.unavailableDescription",
      retry: "admin.taxRates.retry",
      checklist: {
        routeReachable: "admin.taxRates.checklist.routeReachable",
        tableMigrated: "admin.taxRates.checklist.tableMigrated",
        retryHealthy: "admin.taxRates.checklist.retryHealthy",
      },
      supportingContent: "admin.taxRates.supportingContent",
      refresh: "admin.taxRates.refresh",
      messages: {
        unavailable: "admin.taxRates.messages.unavailable",
        createError: "admin.taxRates.messages.createError",
        saveError: "admin.taxRates.messages.saveError",
        deleteError: "admin.taxRates.messages.deleteError",
      },
    },
    tenantMigration: {
      title: "admin.tenantMigration.title",
      description: "admin.tenantMigration.description",
      activeMigrations: "admin.tenantMigration.activeMigrations",
      startError: "admin.tenantMigration.startError",
      exportError: "admin.tenantMigration.exportError",
      confirmPrefix: "admin.tenantMigration.confirmPrefix",
      confirmSuffix: "admin.tenantMigration.confirmSuffix",
      tierLabels: {
        tier1Hetzner: "admin.tenantMigration.tierLabels.tier1Hetzner",
        tier2Shared: "admin.tenantMigration.tierLabels.tier2Shared",
        tier3Neon: "admin.tenantMigration.tierLabels.tier3Neon",
      },
      table: {
        tenant: "admin.tenantMigration.table.tenant",
        tier: "admin.tenantMigration.table.tier",
        dbSize: "admin.tenantMigration.table.dbSize",
        pg: "admin.tenantMigration.table.pg",
        schema: "admin.tenantMigration.table.schema",
        status: "admin.tenantMigration.table.status",
        actions: "admin.tenantMigration.table.actions",
      },
      actions: {
        tier2ToTier1: "admin.tenantMigration.actions.tier2ToTier1",
        tier1ToTier3: "admin.tenantMigration.actions.tier1ToTier3",
        tier3ToTier1: "admin.tenantMigration.actions.tier3ToTier1",
      },
      portabilityNotice: "admin.tenantMigration.portabilityNotice",
    },
    aiAgents: {
      title: "admin.aiAgents.title",
      views: {
        agents: "admin.aiAgents.views.agents",
        performance: "admin.aiAgents.views.performance",
        packs: "admin.aiAgents.views.packs",
        kb: "admin.aiAgents.views.kb",
      },
      performance: {
        agent: "admin.aiAgents.performance.agent",
        calls: "admin.aiAgents.performance.calls",
        success: "admin.aiAgents.performance.success",
        latency: "admin.aiAgents.performance.latency",
        feedback: "admin.aiAgents.performance.feedback",
      },
      packs: {
        rulesSuffix: "admin.aiAgents.packs.rulesSuffix",
        active: "admin.aiAgents.packs.active",
        disabled: "admin.aiAgents.packs.disabled",
      },
      knowledgeBase: {
        total: "admin.aiAgents.knowledgeBase.total",
        embedded: "admin.aiAgents.knowledgeBase.embedded",
        pending: "admin.aiAgents.knowledgeBase.pending",
        failed: "admin.aiAgents.knowledgeBase.failed",
        vectorSearchPreview: "admin.aiAgents.knowledgeBase.vectorSearchPreview",
        searchPlaceholder: "admin.aiAgents.knowledgeBase.searchPlaceholder",
        search: "admin.aiAgents.knowledgeBase.search",
        scorePrefix: "admin.aiAgents.knowledgeBase.scorePrefix",
      },
    },
    businessFeatures: {
      security: {
        title: "admin.businessFeatures.security.title",
        score: "admin.businessFeatures.security.score",
        mfa: "admin.businessFeatures.security.mfa",
        sso: "admin.businessFeatures.security.sso",
        ipWhitelist: "admin.businessFeatures.security.ipWhitelist",
        encryption: "admin.businessFeatures.security.encryption",
        openVulnerabilitiesSuffix: "admin.businessFeatures.security.openVulnerabilitiesSuffix",
      },
      usage: {
        titlePrefix: "admin.businessFeatures.usage.titlePrefix",
        contacts: "admin.businessFeatures.usage.contacts",
        automations: "admin.businessFeatures.usage.automations",
        emailSends: "admin.businessFeatures.usage.emailSends",
        aiRequests: "admin.businessFeatures.usage.aiRequests",
        storage: "admin.businessFeatures.usage.storage",
        projectedOveragePrefix: "admin.businessFeatures.usage.projectedOveragePrefix",
        upgradePlan: "admin.businessFeatures.usage.upgradePlan",
      },
      approvals: {
        title: "admin.businessFeatures.approvals.title",
        shortcuts: "admin.businessFeatures.approvals.shortcuts",
        empty: "admin.businessFeatures.approvals.empty",
        approve: "admin.businessFeatures.approvals.approve",
        reject: "admin.businessFeatures.approvals.reject",
      },
      transfers: {
        title: "admin.businessFeatures.transfers.title",
        handoffTitle: "admin.businessFeatures.transfers.handoffTitle",
        handoffDescription: "admin.businessFeatures.transfers.handoffDescription",
        handoffAction: "admin.businessFeatures.transfers.handoffAction",
        bulkTitle: "admin.businessFeatures.transfers.bulkTitle",
        bulkDescription: "admin.businessFeatures.transfers.bulkDescription",
        bulkAction: "admin.businessFeatures.transfers.bulkAction",
      },
    },
    featureFlags: {
      title: "admin.featureFlags.title",
      description: "admin.featureFlags.description",
      createFlag: "admin.featureFlags.createFlag",
      unavailableTitle: "admin.featureFlags.unavailableTitle",
      unavailableDescription: "admin.featureFlags.unavailableDescription",
      retry: "admin.featureFlags.retry",
      table: {
        flag: "admin.featureFlags.table.flag",
        status: "admin.featureFlags.table.status",
        rollout: "admin.featureFlags.table.rollout",
        owner: "admin.featureFlags.table.owner",
        updated: "admin.featureFlags.table.updated",
        actions: "admin.featureFlags.table.actions",
      },
      actions: {
        edit: "admin.featureFlags.actions.edit",
        tenantOverride: "admin.featureFlags.actions.tenantOverride",
        kill: "admin.featureFlags.actions.kill",
      },
      empty: "admin.featureFlags.empty",
      tenantOverride: {
        title: "admin.featureFlags.tenantOverride.title",
        description: "admin.featureFlags.tenantOverride.description",
        close: "admin.featureFlags.tenantOverride.close",
        tenantLabel: "admin.featureFlags.tenantOverride.tenantLabel",
        loadingTenants: "admin.featureFlags.tenantOverride.loadingTenants",
        enabled: "admin.featureFlags.tenantOverride.enabled",
        enabledHint: "admin.featureFlags.tenantOverride.enabledHint",
        valueLabel: "admin.featureFlags.tenantOverride.valueLabel",
        valueHint: "admin.featureFlags.tenantOverride.valueHint",
        reasonLabel: "admin.featureFlags.tenantOverride.reasonLabel",
        reasonPlaceholder: "admin.featureFlags.tenantOverride.reasonPlaceholder",
        tenantRequired: "admin.featureFlags.tenantOverride.tenantRequired",
        valueJsonError: "admin.featureFlags.tenantOverride.valueJsonError",
        loadError: "admin.featureFlags.tenantOverride.loadError",
        saveError: "admin.featureFlags.tenantOverride.saveError",
        removeError: "admin.featureFlags.tenantOverride.removeError",
        saveSuccess: "admin.featureFlags.tenantOverride.saveSuccess",
        removeSuccess: "admin.featureFlags.tenantOverride.removeSuccess",
        saving: "admin.featureFlags.tenantOverride.saving",
        removing: "admin.featureFlags.tenantOverride.removing",
        save: "admin.featureFlags.tenantOverride.save",
        remove: "admin.featureFlags.tenantOverride.remove",
      },
      editForm: {
        title: "admin.featureFlags.editForm.title",
        description: "admin.featureFlags.editForm.description",
        close: "admin.featureFlags.editForm.close",
        flagName: "admin.featureFlags.editForm.flagName",
        flagKey: "admin.featureFlags.editForm.flagKey",
        descriptionLabel: "admin.featureFlags.editForm.descriptionLabel",
        enabled: "admin.featureFlags.editForm.enabled",
        enabledHint: "admin.featureFlags.editForm.enabledHint",
        rolloutPercentage: "admin.featureFlags.editForm.rolloutPercentage",
        targetingRules: "admin.featureFlags.editForm.targetingRules",
        targetingHint: "admin.featureFlags.editForm.targetingHint",
        jsonError: "admin.featureFlags.editForm.jsonError",
        rangeError: "admin.featureFlags.editForm.rangeError",
        saveError: "admin.featureFlags.editForm.saveError",
        saving: "admin.featureFlags.editForm.saving",
        saveChanges: "admin.featureFlags.editForm.saveChanges",
        cancel: "admin.featureFlags.editForm.cancel",
      },
      createForm: {
        title: "admin.featureFlags.createForm.title",
        keyLabel: "admin.featureFlags.createForm.keyLabel",
        keyPlaceholder: "admin.featureFlags.createForm.keyPlaceholder",
        nameLabel: "admin.featureFlags.createForm.nameLabel",
        namePlaceholder: "admin.featureFlags.createForm.namePlaceholder",
        creating: "admin.featureFlags.createForm.creating",
        create: "admin.featureFlags.createForm.create",
        cancel: "admin.featureFlags.createForm.cancel",
      },
    },
  },

  agency: {
    firstClient: {
      title: "agency.firstClient.title",
      description: "agency.firstClient.description",
      nextTitle: "agency.firstClient.nextTitle",
      steps: {
        businessInfo: "agency.firstClient.steps.businessInfo",
        preconfigure: "agency.firstClient.steps.preconfigure",
        dashboard: "agency.firstClient.steps.dashboard",
      },
      cta: "agency.firstClient.cta",
      skip: "agency.firstClient.skip",
    },
    personas: {
      title: "agency.personas.title",
      description: "agency.personas.description",
      save: "agency.personas.save",
      saving: "agency.personas.saving",
      success: "agency.personas.success",
      empty: "agency.personas.empty",
    },
    profitability: {
      title: "agency.profitability.title",
      emptyTitle: "agency.profitability.emptyTitle",
      emptyDescription: "agency.profitability.emptyDescription",
      kpis: {
        totalRevenue: "agency.profitability.kpis.totalRevenue",
        totalCost: "agency.profitability.kpis.totalCost",
        netMargin: "agency.profitability.kpis.netMargin",
        avgMarginPct: "agency.profitability.kpis.avgMarginPct",
      },
      lowMargin: {
        singular: "agency.profitability.lowMargin.singular",
        plural: "agency.profitability.lowMargin.plural",
        suffix: "agency.profitability.lowMargin.suffix",
      },
      table: {
        client: "agency.profitability.table.client",
        revenue: "agency.profitability.table.revenue",
        cost: "agency.profitability.table.cost",
        margin: "agency.profitability.table.margin",
        percent: "agency.profitability.table.percent",
        trend: "agency.profitability.table.trend",
      },
    },
    attribution: {
      title: "agency.attribution.title",
      description: "agency.attribution.description",
      period: {
        current: "agency.attribution.period.current",
        last: "agency.attribution.period.last",
        quarter: "agency.attribution.period.quarter",
      },
      revenueLabel: "agency.attribution.revenueLabel",
      emptyTitle: "agency.attribution.emptyTitle",
      emptyDescription: "agency.attribution.emptyDescription",
      table: {
        client: "agency.attribution.table.client",
        totalRevenue: "agency.attribution.table.totalRevenue",
        yourShare: "agency.attribution.table.yourShare",
        partner: "agency.attribution.table.partner",
        split: "agency.attribution.table.split",
      },
    },
    proofOfValue: {
      title: "agency.proofOfValue.title",
      exportReport: "agency.proofOfValue.exportReport",
      emptyTitle: "agency.proofOfValue.emptyTitle",
      emptyDescription: "agency.proofOfValue.emptyDescription",
      csv: {
        month: "agency.proofOfValue.csv.month",
        revenue: "agency.proofOfValue.csv.revenue",
        platformAttributed: "agency.proofOfValue.csv.platformAttributed",
      },
      kpis: {
        platformAttributedRevenue: "agency.proofOfValue.kpis.platformAttributedRevenue",
        ofTotalSuffix: "agency.proofOfValue.kpis.ofTotalSuffix",
        trueRoas: "agency.proofOfValue.kpis.trueRoas",
        roasHintPrefix: "agency.proofOfValue.kpis.roasHintPrefix",
        roasHintMiddle: "agency.proofOfValue.kpis.roasHintMiddle",
        platformActivity: "agency.proofOfValue.kpis.platformActivity",
      },
      monthlyRevenueTrend: "agency.proofOfValue.monthlyRevenueTrend",
      legend: {
        attributed: "agency.proofOfValue.legend.attributed",
        other: "agency.proofOfValue.legend.other",
      },
    },
  },
} as const;

// ---------------------------------------------------------------------------
// Type helpers
// ---------------------------------------------------------------------------

/** Recursively extract all leaf string values from a nested object type */
type LeafValues<T> = T extends string
  ? T
  : T extends Record<string, unknown>
    ? { [K in keyof T]: LeafValues<T[K]> }[keyof T]
    : never;

/** All valid message keys — use for type-safe t() calls */
export type MessageKey = LeafValues<typeof MSG>;
